import React, { useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  useColorMode,
  Avatar,
  Flex,
  Badge,
  Icon,
  Divider,
} from '@chakra-ui/react'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  FaQuoteLeft,
  FaStar,
  FaSchool,
  FaUsers,
  FaChartLine,
  FaAward,
} from 'react-icons/fa'
import LEColorConstants, {
  basicTextColor,
} from '@/lib/constants/colorConstants'

const testimonials = [
  {
    name: 'Dr. Nsofor Kaycee',
    position: 'Director, LearningField Schools',
    location: 'Anambra, Nigeria',
    image: '/testimonialSign.png', // Placeholder - replace with actual image
    quote:
      'Since implementing this CBT platform, our JAMB success rate increased from 72% to 94%. Our students are now confident and well-prepared for any digital examination format.',
    rating: 5,
    school: 'LearningField International Schools',
    students: 6000,
  },
  {
    name: 'Sr. Rev Bukola',
    position: 'Head Mistress',
    location: 'Ondo, Nigeria',
    image: '/testimonialSign.png',
    quote:
      'The analytics feature is incredible! We can now identify struggling students early and provide targeted support. Parent confidence in our school has never been higher.',
    rating: 5,
    school: 'St. Louis N/P School, Owo ',
    students: 800,
  },
  // {
  //   name: 'Prof. Emmanuel Udoh',
  //   position: 'School Administrator',
  //   location: 'Port Harcourt, Nigeria',
  //   image: '/testimonialSign.png',
  //   quote:
  //     "This platform transformed our examination process. We've eliminated exam malpractice completely while improving our students' performance dramatically.",
  //   rating: 5,
  //   school: 'Federal Government College',
  //   students: 1500,
  // },
]

const stats = [
  {
    icon: FaSchool,
    number: '200+',
    label: 'Schools Trust Us',
    description: 'Across Nigeria and West Africa',
  },
  {
    icon: FaUsers,
    number: '150K+',
    label: 'Students Prepared',
    description: 'Successfully using our platform',
  },
  {
    icon: FaChartLine,
    number: '40%',
    label: 'Average Score Increase',
    description: 'Improved exam performance',
  },
  {
    icon: FaAward,
    number: '98%',
    label: 'Satisfaction Rate',
    description: 'Schools recommend us',
  },
]

const certifications = [
  'Ministry of Education Approved',
  'JAMB Partnership Program',
  'ISO 27001 Certified',
  'WAEC Aligned Curriculum',
  'NECO Standard Compliant',
]

export const SocialProofSection = () => {
  const { colorMode } = useColorMode()
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')

  const controls = useAnimation()
  const { inView, ref } = useInView({ threshold: 0.2 })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  return (
    <Box
      bg={bgColor}
      py="5rem"
      ref={ref}
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <Container maxW="90rem" mx="auto" px={['1rem', '3rem', '4rem', '6rem']}>
        <VStack spacing="4rem">
          {/* Header */}
          <Box
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Heading
              fontSize={['2rem', '2.5rem', '3rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              Trusted by{' '}
              <Text as="span" color={LEColorConstants.primary}>
                Leading Schools
              </Text>{' '}
              Nationwide
            </Heading>

            <Text
              fontSize={['1.1rem', '1.3rem']}
              color={basicTextColor[colorMode]}
              maxW="4xl"
              mx="auto"
              fontWeight="medium"
            >
              Join hundreds of forward-thinking schools that have already given
              their students the CBT advantage
            </Text>
          </Box>

          {/* Statistics */}
          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing="2rem"
            w="full"
            as={motion.div}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.2,
                },
              },
            }}
          >
            {stats.map((stat, index) => (
              <Box
                key={index}
                textAlign="center"
                as={motion.div}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
              >
                <Icon
                  as={stat.icon}
                  boxSize="48px"
                  color={LEColorConstants.primary}
                  mb="1rem"
                />

                <Text
                  fontSize="3rem"
                  fontWeight="bold"
                  color={basicTextColor[colorMode]}
                  lineHeight="1"
                >
                  {stat.number}
                </Text>

                <Heading
                  size="md"
                  color={basicTextColor[colorMode]}
                  mb="0.5rem"
                >
                  {stat.label}
                </Heading>

                <Text color="gray.600" fontSize="0.9rem">
                  {stat.description}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          {/* Testimonials */}
          <Box w="full">
            <Heading
              textAlign="center"
              fontSize={['1.8rem', '2.2rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="3rem"
              as={motion.div}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, delay: 0.4 },
                },
              }}
            >
              What School Leaders Are Saying
            </Heading>

            <SimpleGrid
              columns={{ base: 1, lg: 3 }}
              spacing="2rem"
              as={motion.div}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.2,
                    delayChildren: 0.6,
                  },
                },
              }}
            >
              {testimonials.map((testimonial, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p="2rem"
                  borderRadius="16px"
                  boxShadow="lg"
                  position="relative"
                  border="1px solid"
                  borderColor="gray.200"
                  as={motion.div}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.5 },
                    },
                  }}
                  _hover={{
                    transform: 'translateY(-5px)',
                    boxShadow: 'xl',
                  }}
                  transition="all 0.3s ease"
                >
                  {/* Quote Icon */}
                  <Icon
                    as={FaQuoteLeft}
                    position="absolute"
                    top="1rem"
                    right="1rem"
                    boxSize="24px"
                    color={LEColorConstants.primary}
                    opacity="0.3"
                  />

                  <VStack align="start" spacing="1rem">
                    {/* Rating */}
                    <HStack>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Icon
                          key={i}
                          as={FaStar}
                          color="yellow.400"
                          boxSize="16px"
                        />
                      ))}
                    </HStack>

                    {/* Quote */}
                    <Text
                      fontSize="1rem"
                      lineHeight="1.6"
                      color={basicTextColor[colorMode]}
                      fontStyle="italic"
                    >
                      "{testimonial.quote}"
                    </Text>

                    <Divider />

                    {/* Author Info */}
                    <Flex align="center" w="full">
                      <Avatar
                        size="md"
                        name={testimonial.name}
                        src={testimonial.image}
                        mr="1rem"
                      />

                      <VStack align="start" spacing="0" flex="1">
                        <Text
                          fontWeight="bold"
                          color={basicTextColor[colorMode]}
                          fontSize="0.95rem"
                        >
                          {testimonial.name}
                        </Text>

                        <Text fontSize="0.85rem" color="gray.600">
                          {testimonial.position}
                        </Text>

                        <Text
                          fontSize="0.8rem"
                          color={LEColorConstants.primary}
                          fontWeight="medium"
                        >
                          {testimonial.school}
                        </Text>
                      </VStack>
                    </Flex>

                    {/* School Stats */}
                    <HStack spacing="1rem" w="full" justify="space-between">
                      <Badge colorScheme="blue" fontSize="0.75rem">
                        📍 {testimonial.location}
                      </Badge>
                      <Badge colorScheme="green" fontSize="0.75rem">
                        👥 {testimonial.students} students
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Certifications */}
          {/* <Box
            w="full"
            bg={cardBg}
            borderRadius="16px"
            p="2rem"
            textAlign="center"
            as={motion.div}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.8 },
              },
            }}
          >
            <Heading
              fontSize={['1.5rem', '1.8rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="2rem"
            >
              🏆 Official Certifications & Partnerships
            </Heading>

            <Flex wrap="wrap" justify="center" align="center" gap="1rem">
              {certifications.map((cert, index) => (
                <Badge
                  key={index}
                  colorScheme="blue"
                  variant="solid"
                  fontSize="0.9rem"
                  py="0.5rem"
                  px="1rem"
                  borderRadius="full"
                >
                  ✓ {cert}
                </Badge>
              ))}
            </Flex>

            <Text fontSize="0.9rem" color="gray.600" mt="1.5rem">
              Fully compliant with Nigerian educational standards and
              international best practices
            </Text>
          </Box> */}

          {/* Trust Indicators */}
          <Box
            w="full"
            bg="linear-gradient(135deg, #009BFF10 0%, #FF6E0610 100%)"
            borderRadius="16px"
            p="2rem"
            textAlign="center"
            border="2px solid"
            borderColor={LEColorConstants.primary}
            as={motion.div}
            variants={{
              hidden: { opacity: 0, scale: 0.95 },
              visible: {
                opacity: 1,
                scale: 1,
                transition: { duration: 0.6, delay: 1 },
              },
            }}
          >
            <Text
              fontSize={['1.2rem', '1.4rem']}
              fontWeight="bold"
              color={basicTextColor[colorMode]}
              mb="1rem"
            >
              🔒 Your School's Success is Guaranteed
            </Text>

            <HStack justify="center" spacing="2rem" flexWrap="wrap">
              <Text fontSize="0.9rem" color="gray.600">
                ✅ 30-Day Money Back
              </Text>
              <Text fontSize="0.9rem" color="gray.600">
                ✅ 99.9% Uptime SLA
              </Text>
              <Text fontSize="0.9rem" color="gray.600">
                ✅ 24/7 Technical Support
              </Text>
              <Text fontSize="0.9rem" color="gray.600">
                ✅ Free Training Included
              </Text>
            </HStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
